import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LINKS, PHOTOS } from "@/data/content";

function TiltBook({ children, testid }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 180, damping: 22 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 180, damping: 22 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      data-testid={testid}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 900 }}
      className="relative w-64 cursor-pointer md:w-72"
    >
      {children}
    </motion.div>
  );
}

export default function Books() {
  return (
    <section id="books" data-testid="books-section" className="relative overflow-hidden bg-[#F7F1E6] py-28 lg:py-40">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -left-8 top-8 select-none font-display text-[16rem] font-light italic leading-none text-[#C5A059]/10"
      >
        04
      </span>

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-20 lg:grid-cols-2">
          <div className="flex flex-wrap items-end justify-center gap-10 lg:justify-start">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            >
              <TiltBook testid="book-infinite-life">
                <img
                  src={PHOTOS.bookCover}
                  alt="Infinite Life, Infinite Lessons by Susan Grau — official Hay House cover"
                  data-testid="book-cover-image"
                  className="aspect-[2/3] w-full object-cover shadow-[0_30px_60px_-20px_rgba(44,38,32,0.35)]"
                  style={{ transform: "translateZ(30px)" }}
                />
              </TiltBook>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
            >
              <TiltBook testid="book-when-shadows-speak">
                <img
                  src="/images/book-shadows-speak.png"
                  alt="When Shadows Speak by Susan Grau — foreword by Valerie Bertinelli, coming late 2027"
                  data-testid="book-shadows-speak-image"
                  className="aspect-[2/3] w-full object-cover shadow-[0_30px_60px_-20px_rgba(107,93,79,0.35)]"
                  style={{ transform: "translateZ(30px)" }}
                />
              </TiltBook>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 60, rotate: 2 }}
              whileInView={{ opacity: 1, y: 0, rotate: 2 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, delay: 0.3, ease: [0.76, 0, 0.24, 1] }}
              className="hidden lg:block"
            >
              <TiltBook testid="book-holding-photo">
                <div
                  className="border border-[#C5A059]/60 bg-[#FDFAF4] p-3 shadow-[0_30px_60px_-20px_rgba(44,38,32,0.3)]"
                  style={{ transform: "translateZ(30px)" }}
                >
                  <img
                    src={PHOTOS.holdingBook}
                    alt="Susan Grau holding Infinite Life, Infinite Lessons"
                    className="aspect-[3/4] w-52 object-cover"
                  />
                  <p className="mt-3 text-center font-script text-xl text-[#C5A059]">the bestseller, in hand</p>
                </div>
              </TiltBook>
            </motion.div>
          </div>

          <div>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
            >
              Chapter 04 — The Books
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
              className="mt-6 font-display text-4xl font-light leading-tight tracking-tighter text-[#6B5D4F] sm:text-5xl lg:text-6xl"
            >
              Words That <span className="italic text-[#C5A059]">Heal Nations</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-8 space-y-6"
            >
              <p className="font-body text-base font-light leading-relaxed text-[#8B7D6B]">
                <span className="font-display text-lg italic text-[#6B5D4F]">Infinite Life, Infinite Lessons: Wisdom from the Spirit World on Living, Dying, and the In-Between</span> (Hay
                House) is Susan's international bestseller — over 10,000 copies sold and counting, with foreign
                editions in Turkey and Spain. Its signature "Three A's" framework — Awareness, Acceptance, and
                Action — has become a roadmap for navigating loss and transformation. Through hundreds of book
                signings and live appearances, she has
                built a loyal international community united by healing, resilience, and hope.
              </p>
              <p className="font-body text-base font-light leading-relaxed text-[#8B7D6B]">
                Her highly anticipated second book, <span className="font-display text-lg italic text-[#6B5D4F]">When Shadows Speak: Giving a Voice to the Trauma, Grief and Pain That Binds You</span> —
                featuring a foreword by Valerie Bertinelli — arrives in late 2027. A transformational guide to
                shadow work, grief, and trauma, it introduces Susan's groundbreaking framework: our wounds belong
                to the human spirit, while healing occurs through the soul. Each chapter closes with practical
                "Mirror Work" exercises that turn insight into lasting change.
              </p>
            </motion.div>
            <motion.p
              data-testid="book-facts"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#8B7D6B]"
            >
              <span>10,000+ copies sold</span>
              <span className="h-1 w-1 rotate-45 bg-[#C5A059]" aria-hidden="true" />
              <span>Editions in Turkey & Spain</span>
              <span className="h-1 w-1 rotate-45 bg-[#C5A059]" aria-hidden="true" />
              <span>Published by Hay House</span>
              <span className="h-1 w-1 rotate-45 bg-[#C5A059]" aria-hidden="true" />
              <span>Next book foreword: Valerie Bertinelli</span>
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                data-testid="books-amazon-link"
                href={LINKS.amazon}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 bg-[#6B5D4F] px-7 py-3.5 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#FDFAF4] transition-colors duration-500 hover:bg-[#C5A059]"
              >
                Buy on Amazon
                <ArrowUpRight size={15} className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                data-testid="books-bn-link"
                href={LINKS.barnesNoble}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 border border-[#6B5D4F] px-7 py-3.5 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F] transition-colors duration-500 hover:border-[#C5A059] hover:text-[#C5A059]"
              >
                Barnes & Noble
                <ArrowUpRight size={15} className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="mt-10 border-t border-[#6B5D4F]/10 pt-8"
            >
              <p className="font-body text-[0.6rem] font-medium uppercase tracking-[0.4em] text-[#8B7D6B]">
                Available worldwide
              </p>
              <div className="mt-4 flex flex-wrap gap-x-7 gap-y-2.5">
                {[
                  ["Amazon", LINKS.amazon],
                  ["Kindle", LINKS.kindle],
                  ["Barnes & Noble", LINKS.barnesNoble],
                  ["Waterstones", LINKS.waterstones],
                  ["Indigo", LINKS.indigo],
                  ["Booktopia", LINKS.booktopia],
                  ["Books-A-Million", LINKS.booksAMillion],
                ].map(([name, href], i) => (
                  <a
                    key={name}
                    data-testid={`retailer-link-${i}`}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gold-underline pb-0.5 font-body text-[0.7rem] font-medium uppercase tracking-[0.25em] text-[#6B5D4F]"
                  >
                    {name}
                  </a>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
